import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, onlineManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import * as idb from 'idb-keyval';
import { BrowserRouter } from 'react-router-dom';
// Self-hosted premium type stack - bundled with the app so we never depend on
// network access in the packaged build (CSP forbids external fonts anyway).
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
// Display font (Space Grotesk) is only used at medium/semibold in the UI
// (`font-display` always pairs with font-medium/font-semibold), so the 700
// weight was dead payload — dropped. Mono (JetBrains) only ever renders at
// its default 400 weight, so 500 was dropped too. Trimming these two unused
// weights shaves the first-paint font download with no visual change.
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/jetbrains-mono/400.css';
import { App } from './app/App';
import { ConfirmProvider } from './shared/components/ConfirmDialog';
import { startOnlineDetection, subscribeOnlineStatus } from './shared/platform/online-status';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Long stale time + persisted cache = useful offline view. Queries
      // serve from cache while offline, and refresh on the next mount once
      // the network is back.
      staleTime: 30_000,
      gcTime: 7 * 24 * 60 * 60 * 1000, // keep cached for 7 days
      // React Query v5: a failing fetch (e.g. offline) defaults to retrying.
      // Halve that to fail fast so the UI can show "you're offline" sooner.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    },
  },
});

// Persist the entire query cache to IndexedDB so the desktop client can read
// every page offline. Single key under `idb-keyval`'s default store - small,
// no extra DB to manage. The persister handles serialisation and hydration.
const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => idb.get<string>(key).then((v) => v ?? null),
    setItem: (key, value) => idb.set(key, value),
    removeItem: (key) => idb.del(key),
  },
  key: 'tccrm.queryCache',
});

// Kick off connectivity detection - components subscribe via useOnlineStatus.
startOnlineDetection();

// Bridge our online detection into React Query's `onlineManager` so all
// useQuery hooks automatically pause network attempts when offline. Cached
// data continues to be served from the persister. This makes the
// "view-only offline" UX free for every existing query in the codebase.
subscribeOnlineStatus((online) => {
  onlineManager.setOnline(online);
});

const root = createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 7 * 24 * 60 * 60 * 1000,
        // Bump this when the API contract changes so old cached responses
        // don't deserialise into stale-shaped objects.
        buster: 'v1',
      }}
    >
      <BrowserRouter>
        <ConfirmProvider>
          <App />
        </ConfirmProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
